/**
 * Server truth on screen: the 3D canvas, the system-type legend (only
 * types actually present -- a legend coloring nothing is noise),
 * click-to-inspect -- and the REDLINE product: open findings halo
 * their elements red, the findings panel accepts/rejects with
 * one-click fixes, and the SELECTED element is a draggable widget (a
 * drop posts one edit op; the response is the element's instant
 * compliance verdict).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ActiveGeoElement, ElementFilter } from '../../../api';
import { elementMatchesFilter, filterIsActive, filterToSearch } from '../../../api';
import { ACCENT, INK, tint } from '../../../theme';
import { AbsenceBadge, CategorySwatch, ComplianceTag, VERDICT } from '../../../strata';
import { LUX_SCALE_MAX, useIlluminance } from '../lighting';
import {
  useApplyEdit,
  useComplianceReport,
  useDecideFinding,
  useExportIfc,
  useFindings,
  useFixAll,
} from '../redline';
import type { Revision } from '../revisions';
import { useRevisions, useSaveRevision } from '../revisions';
import { FindingsPanel } from './FindingsPanel';
import { RevisionBar } from './RevisionBar';
import { useElements } from '../queries';
import { CanvasVisualizer } from './CanvasVisualizer';

export function useElementsStatus(projectId: string) {
  const { data, isError, isSuccess } = useElements(projectId);
  // isSuccess, never "not loading": a query can sit pending-but-PAUSED
  // (TanStack parks retries while the tab is unfocused or the browser
  // thinks it's offline), and treating that limbo as success painted a
  // lying green "0 elements loaded" while the server was asleep.
  return { count: data?.length ?? 0, isError, isSettled: isSuccess };
}

/** A LineString's plan run, vertical drop, and grade -- the live slope
 * chip's numbers, computed from the element's own geometry. */
function slopeOf(el: ActiveGeoElement): { runM: number; dropM: number; pct: number } | null {
  const geom = el.geometry;
  if (!geom || geom.type !== 'LineString') return null;
  const coords = geom.coordinates as number[][];
  if (!coords || coords.length < 2) return null;
  let runM = 0;
  for (let i = 1; i < coords.length; i++) {
    runM += Math.hypot(coords[i][0] - coords[i - 1][0], coords[i][1] - coords[i - 1][1]);
  }
  const dropM = Math.abs((coords[coords.length - 1][2] ?? 0) - (coords[0][2] ?? 0));
  if (runM === 0) return null;
  return { runM, dropM, pct: (dropM / runM) * 100 };
}

export function ViewerPanel({ projectId }: { projectId: string }) {
  // Viewing a saved revision points the elements query at its
  // IMMUTABLE snapshot corpus; findings and edits stay with the live
  // project, so a snapshot view is read-only by construction.
  const [viewedRevision, setViewedRevision] = useState<Revision | null>(null);
  const activeProjectId = viewedRevision?.snapshot_project_id ?? projectId;
  const viewingLive = viewedRevision === null;

  const { data: elements = [] } = useElements(activeProjectId);
  const { data: findings = [] } = useFindings(projectId);
  const { data: report = null } = useComplianceReport(projectId);
  const { data: revisions = [] } = useRevisions(projectId);
  const applyEdit = useApplyEdit(projectId);
  const decideFinding = useDecideFinding(projectId);
  const fixAll = useFixAll(projectId);
  const exportIfc = useExportIfc(projectId);
  const saveRevision = useSaveRevision(projectId);
  const [selected, setSelected] = useState<ActiveGeoElement | null>(null);
  // The filter IS the URL (APP_SPEC "Filtering is first-class"): the
  // RuleSelector search params the shell's route types; the legend
  // below is the category chip's visual handle. Read via useSearch --
  // no module ever imports the shell.
  const filter = useSearch({ strict: false }) as ElementFilter;
  const navigate = useNavigate();
  const filtered = filterIsActive(filter);
  // The illuminance heatmap layer: part of the DEFAULT VIEW when the
  // project carries real rooms (user ruling 2026-08-04) -- luxPref
  // null means AUTO (on iff authored IfcSpace rooms exist); the
  // toggle records an explicit choice. MEP-only projects stay off by
  // default (one building-scope wash is heavy as an opener).
  const [luxPref, setLuxPref] = useState<boolean | null>(null);
  const hasRooms = useMemo(
    () =>
      elements.some(
        (el) =>
          (el.raw_attributes as { ifc_class?: string } | undefined)?.ifc_class ===
          'IfcSpace',
      ),
    [elements],
  );
  const luxOn = luxPref ?? hasRooms;
  const { data: luxRooms = [] } = useIlluminance(activeProjectId, luxOn);
  // Drilling (APP_SPEC): peels are session gestures (module-private
  // client state by design), and excluded elements GHOST by default
  // -- the toggle flips ghost -> hidden for a clean view.
  const [peeled, setPeeled] = useState<Set<string>>(new Set());
  const [ghostOn, setGhostOn] = useState(true);
  // Snap-to-interest: picking from a LIST jumps the camera to the
  // element (canvas clicks never move the camera -- you're already
  // looking there).
  const [focusTarget, setFocusTarget] = useState<{ originId: string; nonce: number } | null>(
    null,
  );

  // A new project (or a re-ingest) invalidates any prior selection.
  useEffect(() => {
    setSelected(null);
    setViewedRevision(null);
    setLuxPref(null); // back to AUTO: the next project decides itself
    setPeeled(new Set());
  }, [projectId]);
  // A selection the filter no longer shows (or a peel just hid) would
  // be an invisible drag target -- drop it.
  useEffect(() => {
    setSelected((prev) =>
      prev && (!elementMatchesFilter(prev, filter) || peeled.has(prev.origin_id))
        ? null
        : prev,
    );
  }, [filter, peeled]);
  // Refetched elements carry moved geometry -- keep the selection
  // pointed at the fresh row, not a stale snapshot.
  useEffect(() => {
    setSelected((prev) =>
      prev ? elements.find((el) => el.origin_id === prev.origin_id) ?? null : null,
    );
  }, [elements]);

  const presentSystemTypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const el of elements) {
      const key = el.category ?? 'OTHER';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [elements]);

  const visibleElements = useMemo(
    () =>
      elements.filter(
        (el) => elementMatchesFilter(el, filter) && !peeled.has(el.origin_id),
      ),
    [elements, filter, peeled],
  );
  const visibleIds = useMemo(
    () => new Set(visibleElements.map((el) => el.origin_id)),
    [visibleElements],
  );
  // Ghost mode: the canvas draws EVERYTHING, with the excluded set at
  // low opacity; hidden mode passes only the visible subset.
  const ghostIds = useMemo(() => {
    if (!ghostOn) return undefined;
    const ids = new Set<string>();
    for (const el of elements) if (!visibleIds.has(el.origin_id)) ids.add(el.origin_id);
    return ids.size > 0 ? ids : undefined;
  }, [ghostOn, elements, visibleIds]);
  // Every screen honors the same filter: findings narrow with the
  // canvas, and the bulk verbs below act on exactly this set.
  const visibleFindings = useMemo(
    () => (filtered ? findings.filter((f) => visibleIds.has(f.origin_id)) : findings),
    [filtered, findings, visibleIds],
  );
  const violatingIds = useMemo(
    () => new Set(visibleFindings.filter((f) => f.state === 'open').map((f) => f.origin_id)),
    [visibleFindings],
  );
  // The lighting redline as GEOGRAPHY: the open min-illuminance
  // findings supply the failing floor (their own required value --
  // never a frontend constant) and each room's worst point, so the
  // canvas can paint deficit cells red and pin the exact spots.
  const luxRedline = useMemo(() => {
    const lighting = visibleFindings.filter(
      (f) => f.state === 'open' && f.rule_name === 'ies-rp-1-min-illuminance',
    );
    if (lighting.length === 0) return null;
    const floors = lighting
      .map((f) => (f.required as { min_lux?: number }).min_lux)
      .filter((v): v is number => typeof v === 'number');
    const worstPoints = lighting
      .map((f) => (f.measured as { worst_point?: [number, number] }).worst_point)
      .filter((p): p is [number, number] => Array.isArray(p) && p.length === 2);
    if (floors.length === 0) return null;
    return { floorLux: Math.max(...floors), worstPoints };
  }, [visibleFindings]);

  const typeHidden = (systemType: string) =>
    filter.category ? !filter.category.includes(systemType) : false;

  // The legend writes the URL: toggling recomputes the include list;
  // a list covering every present type drops the param (clean URL).
  const toggleType = (systemType: string) => {
    const present = presentSystemTypes.map(([t]) => t);
    const included = filter.category ?? present;
    const next = included.includes(systemType)
      ? included.filter((t) => t !== systemType)
      : [...included, systemType];
    const complete = present.every((t) => next.includes(t));
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...filterToSearch({ ...filter, category: complete ? undefined : next }),
      }),
      replace: true,
    });
  };

  // The elevation band writes the URL like every other chip: the
  // height_band vocabulary's own params, empty input clears a bound.
  const setBand = (key: 'min_z_meters' | 'max_z_meters', value: string) => {
    const parsed = value === '' ? NaN : Number(value);
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...filterToSearch({
          ...filter,
          [key]: Number.isFinite(parsed) ? parsed : undefined,
        }),
      }),
      replace: true,
    });
  };

  const slope = selected ? slopeOf(selected) : null;

  // The popup tells the element's WHOLE story (user-ruled):
  // its own findings ride the already-fetched findings query, and
  // when the illuminance layer is on, the nearest work-plane sample
  // says how much light lands where this element stands.
  const selectedFindings = useMemo(
    () =>
      selected
        ? findings.filter((f) => f.origin_id === selected.origin_id && f.state === 'open')
        : [],
    [selected, findings],
  );
  const luxHere = useMemo(() => {
    if (!selected || !luxOn || luxRooms.length === 0) return null;
    const geom = selected.geometry;
    const first = (c: unknown): number[] | null =>
      Array.isArray(c) ? (typeof c[0] === 'number' ? (c as number[]) : first(c[0])) : null;
    const at = first(geom?.coordinates);
    if (!at) return null;
    let best: number | null = null;
    let bestDist = 0.75; // within 1.5 grid cells of the sample lattice
    for (const room of luxRooms) {
      for (const [px, py, lux] of room.points) {
        const dist = Math.hypot(px - at[0], py - at[1]);
        if (dist < bestDist) {
          bestDist = dist;
          best = lux;
        }
      }
    }
    return best;
  }, [selected, luxOn, luxRooms]);

  const luxPoints = useMemo(
    () => (luxOn ? luxRooms.flatMap((room) => room.points) : null),
    [luxOn, luxRooms],
  );
  const luxSummary = useMemo(() => {
    if (!luxOn || luxRooms.length === 0) return null;
    return {
      rooms: luxRooms.length,
      min: Math.min(...luxRooms.map((r) => r.min_lux)),
      avg: luxRooms.reduce((s, r) => s + r.avg_lux, 0) / luxRooms.length,
    };
  }, [luxOn, luxRooms]);

  return (
    <>
      {viewingLive && (
      <FindingsPanel
        findings={visibleFindings}
        report={report}
        filtered={filtered}
        selectedOriginId={selected?.origin_id ?? null}
        busy={applyEdit.isPending || decideFinding.isPending || fixAll.isPending}
        exporting={exportIfc.isPending}
        onSelect={(originId) => {
          setSelected(elements.find((el) => el.origin_id === originId) ?? null);
          setFocusTarget({ originId, nonce: Date.now() });
        }}
        onAccept={(findingId, apply) => decideFinding.mutate({ findingId, accept: true, apply })}
        onReject={(findingId) => decideFinding.mutate({ findingId, accept: false })}
        onFixAll={() => fixAll.mutate(filtered ? Array.from(visibleIds) : undefined)}
        onExport={() => exportIfc.mutate()}
      />
      )}

      <div
        className="ui-overlay glass-panel"
        style={{ top: 'auto', bottom: '1rem', left: 'auto', right: '1rem', padding: '0.6rem 0.9rem' }}
      >
        <button
          onClick={() => setLuxPref(!luxOn)}
          style={{
            fontSize: '12px', padding: '0.2rem 0.6rem', borderRadius: '4px',
            border: `1px solid ${luxOn ? ACCENT.amber : INK.muted}`,
            background: luxOn ? tint(ACCENT.amber, 0.15) : 'transparent',
            color: luxOn ? ACCENT.amber : INK.secondary, cursor: 'pointer',
          }}
        >
          {luxOn ? 'Illuminance on' : 'Illuminance'}
        </button>
        {luxSummary && (
          <div style={{ marginTop: '0.5rem', fontSize: '12px', color: INK.primary }}>
            <div
              style={{
                height: '8px', borderRadius: '4px', marginBottom: '0.25rem',
                background: `linear-gradient(to right, ${tint(ACCENT.amber, 0.06)}, ${tint(ACCENT.amber, 0.56)})`,
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', color: INK.secondary }}>
              <span>0 lx</span>
              <span>{LUX_SCALE_MAX}+ lx</span>
            </div>
            <div style={{ marginTop: '0.25rem' }}>
              {luxSummary.rooms} {luxSummary.rooms === 1 ? 'zone' : 'rooms'} · min{' '}
              {luxSummary.min.toFixed(0)} lx · avg {luxSummary.avg.toFixed(0)} lx
            </div>
          </div>
        )}
      </div>

      <RevisionBar
        revisions={revisions}
        viewing={viewedRevision}
        saving={saveRevision.isPending}
        onSave={() => saveRevision.mutate()}
        onView={setViewedRevision}
      />

      {presentSystemTypes.length > 0 && (
        <div className="ui-overlay glass-panel" style={{ top: 'auto', bottom: '1rem', left: '1rem', padding: '0.75rem 1rem' }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary, marginBottom: '0.5rem' }}>
            System types
          </div>
          {presentSystemTypes.map(([systemType, count]) => (
            <button
              key={systemType}
              onClick={() => toggleType(systemType)}
              title={typeHidden(systemType) ? 'Show these elements' : 'Hide these elements'}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '13px',
                marginBottom: '0.25rem', background: 'none', border: 'none', padding: 0,
                color: 'inherit', cursor: 'pointer', width: '100%', textAlign: 'left',
                opacity: typeHidden(systemType) ? 0.35 : 1,
              }}
            >
              <CategorySwatch category={systemType} />
              <span style={{ textDecoration: typeHidden(systemType) ? 'line-through' : 'none' }}>
                {systemType}
              </span>
              <span style={{ color: INK.muted }}>({count})</span>
            </button>
          ))}
          <div style={{ marginTop: '0.6rem', borderTop: '1px solid #1f2937', paddingTop: '0.5rem' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary, marginBottom: '0.35rem' }}>
              Elevation band (m)
            </div>
            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
              <input
                type="number"
                step="0.1"
                placeholder="min"
                aria-label="Elevation band minimum, meters"
                value={filter.min_z_meters ?? ''}
                onChange={(e) => setBand('min_z_meters', e.target.value)}
                style={{ width: '62px', padding: '0.2rem', borderRadius: '4px', border: '1px solid #444', fontSize: '12px' }}
              />
              <span style={{ color: INK.muted }}>–</span>
              <input
                type="number"
                step="0.1"
                placeholder="max"
                aria-label="Elevation band maximum, meters"
                value={filter.max_z_meters ?? ''}
                onChange={(e) => setBand('max_z_meters', e.target.value)}
                style={{ width: '62px', padding: '0.2rem', borderRadius: '4px', border: '1px solid #444', fontSize: '12px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setGhostOn((v) => !v)}
                title="Excluded elements: ghost keeps them faint for context; hidden removes them"
                style={{
                  fontSize: '11px', padding: '0.15rem 0.5rem', borderRadius: '4px',
                  border: `1px solid ${INK.faint}`, color: INK.secondary, background: 'none', cursor: 'pointer',
                }}
              >
                excluded: {ghostOn ? 'ghost' : 'hidden'}
              </button>
              {peeled.size > 0 && (
                <button
                  onClick={() => setPeeled(new Set())}
                  style={{
                    fontSize: '11px', padding: '0.15rem 0.5rem', borderRadius: '4px',
                    border: `1px solid ${ACCENT.amber}`, color: ACCENT.amber, background: 'none', cursor: 'pointer',
                  }}
                >
                  {peeled.size} peeled — restore
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div className="ui-overlay glass-panel" style={{ top: '1rem', right: '1rem', left: 'auto', width: '280px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary }}>
              Selected element
            </div>
            <button
              onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', color: INK.secondary, cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <dl style={{ margin: '0.5rem 0 0', fontSize: '13px' }}>
            <dt style={{ color: INK.muted }}>IFC class</dt>
            <dd style={{ margin: '0 0 0.4rem' }}>{selected.raw_attributes?.ifc_class ?? '—'}</dd>
            <dt style={{ color: INK.muted }}>Name</dt>
            <dd style={{ margin: '0 0 0.4rem' }}>{selected.raw_attributes?.element_name ?? '—'}</dd>
            <dt style={{ color: INK.muted }}>System type</dt>
            <dd style={{ margin: '0 0 0.4rem' }}>{selected.category_display ?? '—'}</dd>
            {slope && (
              <>
                <dt style={{ color: INK.muted }}>Slope</dt>
                <dd style={{ margin: '0 0 0.4rem' }}>
                  <ComplianceTag
                    state={violatingIds.has(selected.origin_id) ? 'violating' : 'complying'}
                  >
                    {slope.pct.toFixed(2)}% · {slope.dropM.toFixed(3)} m over {slope.runM.toFixed(2)} m
                  </ComplianceTag>
                </dd>
              </>
            )}
            {selected.object_type && (
              <>
                <dt style={{ color: INK.muted }}>Object type</dt>
                <dd style={{ margin: '0 0 0.4rem', wordBreak: 'break-all' }}>{selected.object_type}</dd>
              </>
            )}
            {selected.tag && (
              <>
                <dt style={{ color: INK.muted }}>Tag</dt>
                <dd style={{ margin: '0 0 0.4rem' }}>{selected.tag}</dd>
              </>
            )}
            {selected.authoring_tool && selected.authoring_tool !== 'UNKNOWN' && (
              <>
                <dt style={{ color: INK.muted }}>Authored in</dt>
                <dd style={{ margin: '0 0 0.4rem' }}>{selected.authoring_tool}</dd>
              </>
            )}
            {luxHere !== null && (
              <>
                <dt style={{ color: INK.muted }}>Illuminance here</dt>
                <dd style={{ margin: '0 0 0.4rem', color: ACCENT.amber }}>
                  ≈ {Math.round(luxHere)} lx on the work plane
                </dd>
              </>
            )}
            {selectedFindings.length > 0 && (
              <>
                <dt style={{ color: INK.muted }}>Open findings ({selectedFindings.length})</dt>
                {selectedFindings.map((f) => (
                  <dd key={f.id} style={{ margin: '0 0 0.4rem', color: VERDICT.violating, fontSize: '12px' }}>
                    {f.rule_name}
                    <span style={{ color: INK.secondary }}>
                      {' — '}
                      {Object.entries(f.measured)
                        .filter(([, v]) => typeof v === 'number')
                        .slice(0, 2)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(', ')}
                      {' vs '}
                      {Object.entries(f.required)
                        .filter(([, v]) => typeof v === 'number')
                        .slice(0, 1)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(', ')}
                    </span>
                  </dd>
                ))}
              </>
            )}
            <dt style={{ color: INK.muted }}>Origin ID</dt>
            <dd style={{ margin: '0 0 0.4rem', wordBreak: 'break-all' }}>{selected.origin_id}</dd>
            {selected.is_proxy && (
              <dd style={{ margin: '0.4rem 0 0' }}>
                <AbsenceBadge
                  label="proxy"
                  reason="Tier 3 vector-fallback classification — no confident ruling yet"
                />
              </dd>
            )}
            <dd style={{ margin: '0.5rem 0 0' }}>
              <button
                onClick={() => {
                  setPeeled((prev) => new Set(prev).add(selected.origin_id));
                }}
                title="Hide this element to reveal what it covers (session-only; restore from the legend)"
                style={{
                  fontSize: '11px', padding: '0.15rem 0.5rem', borderRadius: '4px',
                  border: `1px solid ${ACCENT.amber}`, color: ACCENT.amber, background: 'none', cursor: 'pointer',
                }}
              >
                Peel away
              </button>
            </dd>
            {applyEdit.data?.round_trip_ms !== undefined && (
              <dd style={{ margin: '0.4rem 0 0', color: INK.muted, fontSize: '11px' }}>
                last verdict in {applyEdit.data.round_trip_ms} ms round-trip
              </dd>
            )}
            <dd style={{ margin: '0.4rem 0 0', color: INK.secondary, fontSize: '12px' }}>
              Drag the highlighted element to move it; the redline re-checks on drop.
            </dd>
          </dl>
          {Object.keys(selected.raw_attributes?.property_sets ?? {}).length > 0 && (
            <details style={{ marginTop: '0.5rem', fontSize: '12px' }}>
              <summary style={{ cursor: 'pointer', color: INK.secondary }}>
                Property sets ({Object.keys(selected.raw_attributes?.property_sets ?? {}).length})
              </summary>
              <div style={{ maxHeight: '30vh', overflowY: 'auto', marginTop: '0.4rem' }}>
                {Object.entries(
                  (selected.raw_attributes?.property_sets ?? {}) as Record<string, Record<string, unknown>>,
                ).map(([pset, props]) => (
                  <div key={pset} style={{ marginBottom: '0.5rem' }}>
                    <div style={{ color: INK.secondary, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {pset}
                    </div>
                    {Object.entries(props).map(([key, value]) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem' }}>
                        <span style={{ color: INK.muted }}>{key}</span>
                        <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, backgroundColor: '#0f204b' }}>
        <CanvasVisualizer
          elements={ghostOn ? elements : visibleElements}
          ghostIds={ghostIds}
          focusTarget={focusTarget}
          illuminancePoints={luxPoints}
          luxRedline={viewingLive ? luxRedline : null}
          onElementClick={setSelected}
          violatingIds={viewingLive ? violatingIds : new Set()}
          selectedOriginId={selected?.origin_id ?? null}
          onElementMoved={(originId, dx, dy) => {
            if (!viewingLive) return; // snapshots are immutable
            applyEdit.mutate({ originId, op: 'move', params: { dx_meters: dx, dy_meters: dy } });
          }}
        />
      </div>
    </>
  );
}
